import { useCallback } from "react"
import { ServerInfoApiDataModel } from "../../models/Game.ts"
import {
    SERVER_NAMES_LOWER,
    SERVERS_64_BITS_LOWER,
} from "../../constants/servers.ts"
import { toSentenceCase } from "../../utils/stringUtils.ts"
import { UniquePopulationData } from "../../models/Population"
import Skeleton from "../global/Skeleton.tsx"
import ColoredText from "../global/ColoredText.tsx"
import Stack from "../global/Stack.tsx"
import ServerNavigationCard from "../global/ServerNavigationCard.tsx"
import NavCardCluster from "../global/NavCardCluster.tsx"
import ServerStatusIcon from "../global/ServerStatusIcon.tsx"

interface Props {
    isLoading?: boolean
    isError?: boolean
    serverInfo?: ServerInfoApiDataModel
    uniqueData?: UniquePopulationData
}

const ServerSelectContent = ({
    isLoading,
    isError,
    serverInfo,
    uniqueData,
}: Props) => {
    const getIconForCard = (
        serverName: string,
        serverInfo: ServerInfoApiDataModel
    ) => {
        if (serverInfo === undefined || serverInfo[serverName] === undefined)
            return (
                <ServerStatusIcon
                    type="indeterminate"
                    className="shrinkable-icon"
                />
            )

        switch (serverInfo[serverName].is_online) {
            case true:
                return (
                    <ServerStatusIcon
                        type="online"
                        className="shrinkable-icon"
                    />
                )
            case false:
                return (
                    <ServerStatusIcon
                        type="offline"
                        className="shrinkable-icon"
                    />
                )
        }
    }

    const getContentForCard = (
        serverName: string,
        uniqueData: UniquePopulationData
    ) => {
        if (uniqueData === undefined)
            return (
                <Skeleton width={`${120 + (serverName.length % 3) * 20}px`} />
            )
        const serverData = uniqueData.server_breakdown[serverName]
        if (!serverData) {
            return <ColoredText color="secondary">Info not found</ColoredText>
        }

        const character_count = serverData.unique_character_count
        const guild_count = serverData.unique_guild_count

        return (
            <Stack direction="column">
                <ColoredText color="blue">
                    {character_count.toLocaleString()} characters
                </ColoredText>
                <ColoredText color="orange">
                    {guild_count.toLocaleString()} guilds
                </ColoredText>
            </Stack>
        )
    }

    const serverSelectContent = useCallback(
        (type: "32bit" | "64bit") => {
            const serverNamesSorted = [...SERVER_NAMES_LOWER].sort(
                (serverNameA, serverNameB) =>
                    serverNameA.localeCompare(serverNameB)
            )
            const filteredServerNames = serverNamesSorted.filter(
                (serverName) => {
                    if (type === "32bit") {
                        return (
                            SERVERS_64_BITS_LOWER.includes(serverName) === false
                        )
                    } else if (type === "64bit") {
                        return (
                            SERVERS_64_BITS_LOWER.includes(serverName) === true
                        )
                    }
                    return true
                }
            )

            return filteredServerNames.map((serverName) => (
                <ServerNavigationCard
                    key={serverName}
                    title={toSentenceCase(serverName)}
                    destination={`/servers/${serverName}`}
                    icon={getIconForCard(serverName, serverInfo)}
                    content={getContentForCard(serverName, uniqueData)}
                    silentDisabled
                />
            ))
        },
        [isLoading, isError, serverInfo]
    )

    return <NavCardCluster>{serverSelectContent("64bit")}</NavCardCluster>
}

export default ServerSelectContent
